import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, InputAdornment, Link, Skeleton, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { client, type Application, type Department, type Feature } from '../lib/api';
import { AppStatusChip } from '../components/StatusChip';
import { AppMonogram, PageHeader, Section } from '../components/Page';
import { tokens } from '../theme';

export function AppsPage() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .catalog()
      .then((data) => {
        setDepartments(data.departments);
        setApplications(data.applications);
        setFeatures(data.features);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return applications.filter((app) => {
      if (tab !== 'all' && app.department_id !== tab) return false;
      if (!query) return true;
      const featText = features
        .filter((f) => f.application_id === app.id)
        .map((f) => `${f.title} ${f.detail}`)
        .join(' ');
      return `${app.name} ${app.tagline} ${app.description} ${app.audience} ${app.stack} ${featText}`
        .toLowerCase()
        .includes(query);
    });
  }, [applications, features, q, tab]);

  const count = (deptId: string) =>
    deptId === 'all' ? applications.length : applications.filter((a) => a.department_id === deptId).length;

  return (
    <>
      <PageHeader
        title="Applications"
        subtitle="Every tool IT Lab builds and supports, with what it does, who it’s for, and how it’s made."
        aside={
          <TextField
            label="Search applications"
            placeholder="Name, feature or stack"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            type="search"
            sx={{ width: { xs: '100%', md: 340 }, flex: 'none' }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        }
      />

      <Section py={0}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="Filter by department"
          sx={{ borderBottom: `1px solid ${tokens.divider}` }}
        >
          <Tab value="all" label={`All (${count('all')})`} />
          {departments.map((d) => (
            <Tab key={d.id} value={d.id} label={`${d.name} (${count(d.id)})`} />
          ))}
        </Tabs>
      </Section>

      <Section py={{ xs: 2, md: 2 }} sx={{ pb: { xs: 8, md: 12 } }}>
        {error && <Alert severity="error" sx={{ my: 4 }}>{error}</Alert>}
        {loading &&
          [1, 2].map((i) => <Skeleton key={i} variant="rounded" height={320} sx={{ my: 6 }} />)}
        {!loading && !error && filtered.length === 0 && (
          <Box sx={{ my: 6, p: 5, bgcolor: tokens.subtle, border: `1px solid ${tokens.divider}`, borderRadius: 2 }}>
            <Typography fontWeight={600} fontSize={18} color={tokens.ink}>
              No applications match
            </Typography>
            <Typography color="text.secondary">Try another department or search term.</Typography>
          </Box>
        )}
        {filtered.map((app) => {
          const dept = departments.find((d) => d.id === app.department_id);
          const appFeatures = features.filter((f) => f.application_id === app.id).slice(0, 4);
          return (
            <Box
              component="article"
              key={app.id}
              sx={{
                py: { xs: 5, md: 7 },
                borderBottom: `1px solid ${tokens.divider}`,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 4, md: 6 },
              }}
            >
              <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                <AppMonogram name={app.name} size="hero" />
                <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2, fontSize: 14 }}>
                  {[
                    ['Department', dept?.name ?? '—'],
                    ['For', app.audience || '—'],
                    ['Version', app.version || '—'],
                  ].map(([k, v]) => (
                    <Box key={k}>
                      <Typography component="dt" fontSize={14} color="text.secondary">
                        {k}
                      </Typography>
                      <Typography component="dd" fontSize={14} fontWeight={600} color={tokens.ink} m={0}>
                        {v}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>
              <Stack spacing={2.5} sx={{ flex: 1.3, minWidth: 0 }}>
                <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                  <AppStatusChip status={app.status} />
                  <Typography fontSize={14} color="text.secondary">
                    {app.stack}
                  </Typography>
                </Stack>
                <Typography variant="h2" sx={{ fontSize: { xs: 30, md: 36 }, fontWeight: 800 }}>
                  <Link component={RouterLink} to={`/apps/${app.slug}`} color="inherit" fontWeight="inherit">
                    {app.name}
                  </Link>
                </Typography>
                <Typography fontSize={20} fontWeight={500} color="#344054">
                  {app.tagline}
                </Typography>
                <Typography color="text.secondary">{app.description}</Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '16px 32px' }}
                >
                  {appFeatures.map((f) => (
                    <Box component="li" key={f.id} sx={{ pt: 1.5, borderTop: `2px solid ${tokens.blue}` }}>
                      <Typography fontWeight={600} color={tokens.ink}>
                        {f.title}
                      </Typography>
                      <Typography fontSize={14} color="text.secondary">
                        {f.detail}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Stack direction="row" gap={3} alignItems="center" flexWrap="wrap" pt={1}>
                  {app.url ? (
                    <Button href={app.url} target="_blank" rel="noreferrer" variant="contained" endIcon={<OpenInNewIcon />}>
                      Open {app.name}
                    </Button>
                  ) : (
                    <Button component={RouterLink} to={`/apps/${app.slug}`} variant="contained">
                      View details
                    </Button>
                  )}
                  <Link component={RouterLink} to={app.url ? `/apps/${app.slug}` : '/status'}>
                    {app.url ? 'Details' : 'Service status'}
                  </Link>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Section>

      <Section band="subtle" py={{ xs: 6, md: 9 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack spacing={1}>
            <Typography variant="h2" fontSize={28}>
              Don’t see what you need?
            </Typography>
            <Typography fontSize={18} color="text.secondary">
              Tell us about the problem and we’ll scope it with you.
            </Typography>
          </Stack>
          <Button component={RouterLink} to="/request" variant="contained" size="large" sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}>
            Request an app
          </Button>
        </Stack>
      </Section>
    </>
  );
}
